import { User } from '@/modules/user/entities/user';

interface Dependencies {
  loadMe: (token: string) => Promise<User>;
  commitSession: (token: string, me: User) => void;
}

export interface SessionCompletion {
  setPendingToken: (token: string) => void;
  hasPendingSession: () => boolean;
  clear: () => void;
  complete: () => Promise<void>;
}

/**
 * Separa a validação do OTP do carregamento da sessão.
 *
 * O token aceito no OTP fica apenas em memória (`pendingToken`). O carregamento
 * do `/me` só persiste a sessão quando conclui com sucesso. Se o `/me` falhar,
 * o token pendente é mantido e `complete()` pode ser chamado de novo para
 * repetir SOMENTE o GET — sem reutilizar o código OTP já consumido.
 *
 * Operações concorrentes são protegidas por `inFlight`: uma segunda chamada a
 * `complete()` enquanto a primeira está em andamento reaproveita a mesma
 * promise. Troca de conta ou cancelamento (via `clear()`/novo token) não pode
 * reabrir uma sessão antiga.
 */
export const createSessionCompletion = (deps: Dependencies): SessionCompletion => {
  let pendingToken: string | null = null;
  let inFlight: Promise<void> | null = null;

  return {
    setPendingToken(token: string) {
      pendingToken = token;
    },

    hasPendingSession() {
      return pendingToken !== null;
    },

    clear() {
      pendingToken = null;
    },

    complete(): Promise<void> {
      if (inFlight) {
        return inFlight;
      }

      if (!pendingToken) {
        return Promise.reject(new Error('NO_PENDING_SESSION'));
      }

      const token = pendingToken;

      inFlight = (async () => {
        const me = await deps.loadMe(token);
        // Cancelamento ou troca de conta durante o carregamento não pode
        // reabrir a sessão antiga.
        if (pendingToken !== token) {
          return;
        }
        deps.commitSession(token, me);
        pendingToken = null;
      })().finally(() => {
        inFlight = null;
      });

      return inFlight;
    },
  };
};
